(ns hai.admin-ui.activities.table
  "Activities table"
  (:require
   [hai-model.activity :as activity-model]
   [hai-model.model :as model]
   [hai.ui.components :as c]
   [hai.ui.components.collection.bulk-actions :as bulk-actions]
   [hai.ui.components.collection.filters :as filters]
   [hai.ui.components.collection.form :as col.form]
   [hai.ui.components.collection.settings :as settings]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.components.form.core :as form]
   [hai.ui.components.form.select.single-select :as single-select]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.web-app.layout :as layout]
   [lambdaisland.ornament :as o]
   [malli.util :as mu]))

(def ^:private status-options
  [{:value :activity.status/active :label "Active"}
   {:value :activity.status/parked :label "Parked"}
   {:value :activity.status/done   :label "Done"}])

(defn- status-select [{:activity/keys [uuid status]}]
  [table/table-cell
   [c/SelectRoot
    (cond-> {:onValueChange (fn [value]
                              (>e [:resolve [(list 'activity/update
                                                   {:activity/uuid uuid
                                                    :activity/status (keyword value)})]]))}
      status (assoc :defaultValue (str (namespace status) "/" (name status))))
    [c/SelectTrigger {:placeholder "—"}]
    [c/SelectContent
     (for [{:keys [value label]} status-options]
       ^{:key value}
       [c/SelectItem {:value (str (namespace value) "/" (name value))} label])]]])

(defn- format-minutes [mins]
  (cond
    (nil? mins) "—"
    (< mins 60) (str mins " min")
    :else (let [hours (quot mins 60)
                remaining (mod mins 60)]
            (if (zero? remaining)
              (str hours " hr")
              (str hours " hr " remaining " min")))))

(defn- format-relative [inst]
  (when inst
    (let [now (.now js/Date)
          ms (- now (.getTime (js/Date. inst)))
          mins (Math/floor (/ ms 60000))
          hours (Math/floor (/ mins 60))
          remaining-mins (mod mins 60)
          days (Math/floor (/ hours 24))
          remaining-hours (mod hours 24)]
      (cond
        (< mins 1)   "just now"
        (< mins 60)  (str mins " min ago")
        (< hours 24) (if (zero? remaining-mins)
                       (str hours " hr ago")
                       (str hours " hr " remaining-mins " min ago"))
        (< days 7)   (if (zero? remaining-hours)
                       (str days " d ago")
                       (str days " d " remaining-hours " hr ago"))
        :else        (.toLocaleDateString (js/Date. inst))))))

(def ^:private recency-unit-options
  [{:value :days :title "days"}
   {:value :hours :title "hours"}])

(o/defstyled settings-render :div
  :flex :flex-col :gap-4
  [:.section :flex :flex-col :gap-1]
  [:.row :flex :flex-row :items-center :gap-2 :mt-1]
  ([{{:keys [recency-value recency-unit active-threshold-minutes
             admin? case-sensitive?]
     :collection/keys [view]} :form/fields}]
   [:<>
    [form/render-field view]
    [c/Separator]
    [:div.section
     [c/Text {:weight "bold"} "Show activities from"]
     [c/Text {:size "2" :color "gray"} "Only show activities you used within this time period"]
     [:div.row
      [form/render-field recency-value]
      [form/render-field recency-unit]]]
    [:div.section
     [c/Text {:weight "bold"} "Time to become active"]
     [c/Text {:size "2" :color "gray"}
      "How many minutes do you need to spend in an activity before it counts as active?"]
     [:div.row
      [form/render-field active-threshold-minutes]
      [c/Text "minutes"]]]
    [c/Separator]
    [:div.section
     [c/Text {:weight "medium" :size "2"} "Advanced"]
     [:div.row
      [form/checkbox admin?]
      [c/Text "Admin view"]]
     [:div.row
      [form/checkbox case-sensitive?]
      [c/Text "Case sensitive sorting, search and filtering"]]]]))

(defn- settings-schema [table-id user]
  (let [[saved-recency-value saved-recency-unit] (:user-config/activity-recency user)
        [saved-threshold-value] (:user-config/activity-active-threshold user)
        reload (fn [& _args] (>e [:settings/reload-table table-id]))
        save-and-reload (fn [_value _field form]
                          (let [field-value (fn [k] (get-in form [:form/fields k :form.field/current-value]))
                                params {:recency-value (field-value :recency-value)
                                        :recency-unit (field-value :recency-unit)
                                        :active-threshold-value (field-value :active-threshold-minutes)
                                        :active-threshold-unit :minutes}]
                            (>e [:resolve [(list 'activity/update-settings params)]
                                 {:on-success-fx [[:dispatch [:settings/reload-table table-id]]]}])))]
    [:map
     [:collection/view
      {:default :collection.view/table
       :form.field/render (fn [view]
                            [:div.row
                             [c/Text "View mode"]
                             [single-select/field-slim view]])
       :form.field.select/options settings/view-options
       :on-change reload}
      (into [:enum] (map :value) settings/view-options)]
     [:recency-value
      {:default (or saved-recency-value (first activity-model/default-recency))
       :on-change save-and-reload
       :form.field/render (fn [field]
                            [c/TextFieldRoot {:type "number"
                                              :min 1
                                              :style {:width "80px"}
                                              :defaultValue (:form.field/current-value field)
                                              :onChange (fn [e]
                                                         (let [v (js/parseInt (.. e -target -value) 10)]
                                                           (when-not (js/isNaN v)
                                                             ((:form.field/set-value! field) v))))}])}
      :int]
     [:recency-unit
      {:default (or saved-recency-unit (second activity-model/default-recency))
       :on-change save-and-reload
       :form.field/render (fn [field] [single-select/field-slim field])
       :form.field.select/options recency-unit-options}
      [:enum :days :hours]]
     [:active-threshold-minutes
      {:default (or saved-threshold-value (first activity-model/default-active-threshold))
       :on-change save-and-reload
       :form.field/render (fn [field]
                            [c/TextFieldRoot {:type "number"
                                              :min 1
                                              :style {:width "80px"}
                                              :defaultValue (:form.field/current-value field)
                                              :onChange (fn [e]
                                                         (let [v (js/parseInt (.. e -target -value) 10)]
                                                           (when-not (js/isNaN v)
                                                             ((:form.field/set-value! field) v))))}])}
      :int]
     [:admin?
      {:default false
       :on-change reload}
      :boolean]
     [:case-sensitive?
      {:default false
       :on-change reload}
      :boolean]]))

(def ^:private ActivityEditSchema
  (mu/select-keys model/Activity [:activity/uuid :activity/title]))

(defn table []
  (let [user @(<s :auth/identity)]
    [collection/component
     {:id ::table
      :schema ActivityEditSchema
      :update-mutation 'activity/update
      :editable? true
      :settings/render settings-render
      :settings/schema (settings-schema ::table user)
      :query [{:activities/table
               [{:collection/items [:activity/uuid
                                    :activity/url
                                    :activity/title
                                    :activity/status
                                    :activity/duration-minutes
                                    :activity/last-active]}]}]
      :paginate? true
      :no-items (fn [_] [:em "No activities tracked yet"])
      :card (fn [_ _] [:div])
      :columns [{:header "Title"
                 :attr :activity/title
                 :field col.form/input
                 :sort? true
                 :filter (filters/text-search {:placeholder "Title..."})}
                {:header ""
                 :attr :activity/url
                 :sort? false
                 :render (fn [{:activity/keys [url]}]
                           [table/table-cell
                            [c/Link {:href (str "https://" url)
                                     :target "_blank"}
                             [icons/outlined-open_in_new]]])}
                {:header "Active time"
                 :attr :activity/duration-minutes
                 :sort? false
                 :render (fn [{:activity/keys [duration-minutes]}]
                           [table/table-cell
                            (format-minutes duration-minutes)])}
                {:header "Last active"
                 :attr :activity/last-active
                 :sort? false
                 :render (fn [{:activity/keys [last-active]}]
                           [table/table-cell
                            (format-relative last-active)])}
                {:header "Status"
                 :attr :activity/status
                 :sort? false
                 :render status-select
                 :filter {:schema :keyword
                          :type :filter.remote/select
                          :placeholder "Status..."
                          :field single-select/field}}]
      :bulk-actions [{:title "Delete"
                      :mutation 'activity/delete
                      :alert {:content (fn [{:table/keys [selected] :as tbl} action]
                                         [bulk-actions/confirm-dialog
                                          {:table  tbl
                                           :action action}
                                          [bulk-actions/title "Confirm Deletion"]
                                          [bulk-actions/description "Are you sure you want to delete the following activities?"]
                                          (into [:ul]
                                                (map (fn [{:activity/keys [title url]}]
                                                       [:li [:strong (or title url)]]))
                                                selected)])}}]}]))

(defn page [_]
  [layout/page
   [layout/breadcrumbs]
   [layout/heading]
   [layout/content
    [table]]])
